import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState, actions } from "../../state/reducer";
import { Controller, useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { backend, SourceData } from "../../services/backend";
import {
  CustomerDataToUpdateSchema,
  UpdateUserCustomerType,
} from "../../forms/editCustomer";
import { SelectInput, Textarea, TextInput } from "../components";

interface ModalCreatorProps {
  onSave: (data: UpdateUserCustomerType) => void;
  closeModal: () => void;
}

const CustomerEditorModal: React.FC<ModalCreatorProps> = ({
  onSave,
  closeModal,
}) => {
  const State = useSelector((state: RootState) => state);
  const dispatch = useDispatch();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    control,
  } = useForm<UpdateUserCustomerType>({
    resolver: zodResolver(CustomerDataToUpdateSchema),
    mode: "onChange",
    defaultValues: {
      ...State.customerDetail,
      source_id: State.customerDetail?.source?.id
        ? String(State.customerDetail?.source?.id)
        : undefined,
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "metadata",
  });

  console.log("Ошибки валидации:", errors);

  const [metaDataToDelete, setMetaDataToDelete] = useState<string[]>([]);
  const [sources, setSources] = useState<SourceData[]>([]);

  useEffect(() => {
    backend.getCustomerSources().then((res) => {
      setSources(res.sources);
    });
  }, []);

  const onSubmit = async (data: UpdateUserCustomerType) => {
    if (!isValid) {
      console.log("Data is invalid:", errors);
      return;
    }
    console.log("Submit called");
    try {
      const payload = {
        ...data,
        customer_id: State.customerDetail?.id,
        metaDataToDelete,
      };
      await backend
        .editCustomer(payload)
        .then(() => {
          onSave(payload);
        })
        .catch(() => {
          toast("Ошибка редактирования данных");
        });
      closeModal();
    } catch (error) {
      console.error(error);
      toast.error("Ошибка при редактировании клиента");
    }
  };

  const handleAddMetadata = () => {
    append({ key: "", value: "" });
  };

  const handleRemoveMetadata = (index: number, key?: string) => {
    if (key) {
      setMetaDataToDelete((prev) => [...prev, key]);
    }
    remove(index);
  };

  return (
    <div className="creationForm">
      <div className="modalHeading">
        <h3>Редактирование клиента</h3>
      </div>
      <form onSubmit={handleSubmit(onSubmit)} className="columnCenter">
        <TextInput
          label="Имя"
          registration={register("first_name")}
          required
          error={errors.first_name}
        />

        <TextInput
          label="Фамилия"
          registration={register("last_name")}
          error={errors.last_name}
        />

        <TextInput
          label="Телефон"
          registration={register("phone")}
          required
          error={errors.phone}
        />

        <Textarea
          label="Описание"
          registration={register("description")}
          error={errors.description}
        />

        <SelectInput
          label="Источник"
          registration={register("source_id")}
          options={[
            { value: "1", label: "Нет" },
            ...sources.map((source) => ({
              value: String(source.id),
              label: source.name,
            })),
          ]}
          error={errors.source_id}
        />

        {/* Метаданные */}
        <div className="formField formRow commentRow gap12">
          <label>Дополнительная информация</label>
          <div className="flex columnCenter w100 gap12">
            {fields.map((field, index) => (
              <div key={field.id} className="flex metadataRow w100 gap12">
                <input
                  {...register(`metadata.${index}.key`)}
                  placeholder="Параметр"
                />
                <input
                  {...register(`metadata.${index}.value`)}
                  placeholder="Значение"
                />

                <button
                  type="button"
                  onClick={() => handleRemoveMetadata(index, field.key)}
                  className="removeButton"
                >
                  Удалить
                </button>
              </div>
            ))}
            <div className="flex w100">
              <button
                className="filterButton"
                type="button"
                onClick={handleAddMetadata}
              >
                Добавить данные
              </button>
            </div>
          </div>
        </div>

        <Textarea
          label="Комментарий"
          registration={register("comment")}
          error={errors.comment}
        />

        <div className="flex between btnRow">
          <button className="filterButton" type="submit">
            Сохранить
          </button>
          <button onClick={closeModal} className="cancelButton" type="button">
            Отменить
          </button>
        </div>
      </form>
    </div>
  );
};

export default CustomerEditorModal;
